import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface LocationMapProps {
  center: [number, number];
  bounds?: [[number, number], [number, number]];
  markers: Array<{
    position: [number, number];
    popup: React.ReactNode;
    zipCode: string;
  }>;
  onMarkerClick: (zipCode: string) => void;
}

// Custom marker icon for better visibility
const markerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
  shadowAnchor: [12, 41]
});

function MapBounds({ bounds }: { bounds?: [[number, number], [number, number]] }) {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds);
      // Add some padding to ensure markers are visible
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [bounds, map]);

  return null;
}

export function LocationMap({ center, bounds, markers, onMarkerClick }: LocationMapProps) {
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (mapRef.current && bounds) {
      mapRef.current.fitBounds(bounds, { padding: [100, 100] });
    }
  }, [bounds]);
  return (
    <div className="h-[500px] w-full rounded-lg overflow-hidden shadow-md relative">
      <MapContainer
        center={center}
        zoom={13}
        scrollWheelZoom={false}
        doubleClickZoom={true}
        dragging={true}
        className="h-full w-full"
        whenCreated={map => {
          mapRef.current = map;
          if (bounds) {
            map.fitBounds(bounds, { padding: [100, 100] });
          }
        }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        />
        {markers.map((marker) => (
          <Marker
            key={marker.zipCode}
            position={marker.position}
            icon={markerIcon}
            title={`ZIP Code: ${marker.zipCode}`}
            eventHandlers={{
              click: () => {
                const popup = mapRef.current?.getPane('popupPane');
                if (popup) popup.style.zIndex = '1000';
                onMarkerClick(marker.zipCode);
              }
            }}
          >
            <Popup>
              {marker.popup}
            </Popup>
          </Marker>
        ))}
        {bounds && <MapBounds bounds={bounds} />}
      </MapContainer>
    </div>
  );
}